/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useState, useEffect } from 'react';

import { type ImageMixItem } from '@coze-common/chat-core';
import {
  type IOnImageClickParams,
  type IMessage,
} from '@coze-common/chat-uikit-shared';

import { SingleImageContentWithAutoSize } from '../single-image-content/auto-size';
import { ImageBox } from '../image-content/image-box';
import { typeSafeMessageBoxInnerVariants } from '../../../variants/message-box-inner-variants';
import { makeFakeImageMessage } from '../../../utils/make-fake-image-message';

interface ImageItemListProps {
  imageItemList: ImageMixItem[];
  message: IMessage;
  onImageClick?: (params: IOnImageClickParams) => void;
}

export const ImageItemList: FC<ImageItemListProps> = ({
  imageItemList,
  message,
  onImageClick,
}) => {
  // rokid 图片url
  const [imageUrl, setImageUrl] = useState<string>("");

  // rokid 图片宽高
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 }
  );

  const handleImageClick = (originUrl: string) => {
    onImageClick?.({ message, extra: { url: originUrl } });
  };

  // rokid 图片url是否为http开头
  const isHttpUrl = (url: string) => url.startsWith("http");

  // rokid 获取图片宽高
  const getImageSize = (url: string) => {
    return new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
    });
  };

  // rokid 图片url更新时，获取图片宽高
  useEffect(() => {
    const url = isHttpUrl(imageItemList[0].image.key)
      ? imageItemList[0].image.key
      : imageItemList[0].image.image_ori?.url;
    if (url) {
      getImageSize(url).then((size) => {
        setImageUrl(url);
        setImageSize({
          width: size.width,
          height: size.height,
        });
      });
    }
  }, [imageItemList]);

  return (
    <>
      {Boolean(imageItemList.length) &&
        (imageItemList.length === 1 ? (
          <SingleImageContentWithAutoSize
            // rokid 图片key作为url
            // key={imageItemList[0].image.image_thumb.url}
            key={imageUrl}
            message={makeFakeImageMessage({
              originMessage: message,
              // rokid 图片key作为url
              // url: imageItemList[0].image.image_ori.url,
              // key: imageItemList[0].image.image_ori.url,
              // width: imageItemList[0].image.image_ori.width,
              // height: imageItemList[0].image.image_ori.height,
              url: imageUrl,
              key: imageUrl,
              width: imageSize.width,
              height: imageSize.height,
            })}
            onImageClick={onImageClick}
            className="mb-[16px] rounded-[16px] overflow-hidden"
          />
        ) : (
          <div
            // Here we borrow the style of messageBoxInner
            className={typeSafeMessageBoxInnerVariants({
              color: "whiteness",
              border: null,
              tight: true,
              showBackground: false,
            })}
            style={{ width: "240px" }}
            key={imageItemList[0].image.image_thumb.url}>
            <ImageBox
              data={{ image_list: imageItemList.map((item) => item.image) }}
              eventCallbacks={{
                onImageClick: (_, eventData) =>
                  handleImageClick(eventData.src ?? ""),
              }}
            />
          </div>
        ))}
    </>
  );
};
